import type { ChangeEvent } from 'react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { Button } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { SearchIcon } from '@icons/SearchIcon';
import type { SubscriptionCountStatKey } from '@repositories/SubscriptionCountStatRepository';
import type { Filters } from '@repositories/subscriptionRepository/subscription.types';
import { useLocalStorage } from '@customHooks/useLocalStorage';
import { useSearchParamsFilter } from '@customHooks/useSearchParamsFilter';
import { useGetSubscriptionListInfinite, useSubscriptionCountStat } from '@queryHooks/index';
import { useProductRequestVendor } from '@queryHooks/useProductRequest';
import { DomUtil } from '@utils/DomUtil';
import { ObjectUtil } from '@utils/ObjectUtil';
import type { SubscriptionListModel } from '@models/SubscriptionListModel';
import { FilterSelect as CustomFilterSelect } from '../userMgmt/list/FilterSelect';
import { SubscriptionFilterCard } from './SubscriptionFilterCard';
// MZP2-6364: 레거시 체인 — SubscriptionList.legacy → SubscriptionTable.legacy → SubscriptionRow.legacy
import { SubscriptionTableLegacy as SubscriptionTable } from './subscriptionTable/SubscriptionTable.legacy';
import { AddExistingSubscriptionModal } from './subscriptionTable/addExistingSubscriptionModal';

// MZP2-6364: 1780a74 시점 레거시 SubscriptionList. KTDS 환경(VITE_KTDS_ENABLE_NEW_UI=false)에서만 사용.
export const SubscriptionListLegacy: React.FC = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { tenantId } = useParams();
  const [scrollTarget, setScrollTarget] = useState<HTMLElement | null>(null);

  /**
   * TODO: 맥북 Sonoma에서 dayjs.tz.guess()가 undefined를 반환하는 버그가 있어서 임시로 처리한 코드
   * 참고: https://github.com/iamkun/dayjs/issues/2448
   */
  const [customTZ] = useLocalStorage('customTimezone', 'Asia/Seoul');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const handleAddModal = {
    open: () => {
      setIsAddModalOpen(true);
    },
    close: () => {
      setIsAddModalOpen(false);
    },
  };
  const [clickedType, setClickedType] = useState<SubscriptionCountStatKey>('totalCount');

  const [queries, setQueries, clearQueries] = useSearchParamsFilter<Filters>({
    defaultFilter: {
      keyword: undefined,
      sort: ['hasError.desc'],
      includesSelf: true,
      productVendorName: undefined,
    },
    whiteList: [
      'sort',
      'keyword',
      'softwareId',
      'startDateFrom',
      'startDateTo',
      'endDateFrom',
      'endDateTo',
      'productVendorName',
    ],
  });

  const { data: subscriptionCountStat, isSuccess } = useSubscriptionCountStat(dayjs.tz.guess() ?? customTZ);
  // MZP2-6364: 현재 Filters.status 는 string | string[] (develop). 레거시 로직은 단일 값만 처리하므로 배열은 첫 요소로 좁힘.
  const getSubscriptionStatusQueryCondition = (status: string | string[] | undefined) => {
    const singleStatus = Array.isArray(status) ? status[0] : status;
    switch (singleStatus) {
      case 'ALL':
        return 'OPEN,REQUESTED,ACTIVE,INACTIVE';
      case 'OPEN':
        return 'OPEN,REQUESTED';
      default:
        return singleStatus;
    }
  };

  const {
    isLoading: isSubscriptionListPageLoading,
    data: subscriptionListPage,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetSubscriptionListInfinite({
    ...queries,
    status: getSubscriptionStatusQueryCondition(queries.status),
  });

  const {
    data: productRequestVendorList,
    isFetchingNextPage: isProductVendorFetchingNextPage,
    hasNextPage: isProductVendorHasNextPage,
    fetchNextPage: fetchNextProductVendorPage,
    isLoading: isProductVendorLoading,
  } = useProductRequestVendor({
    page: 0,
    size: 100,
    tenantId: tenantId ?? '',
  });

  const productRequestVendorListItems = productRequestVendorList?.pages.flatMap(({ content }) => content) ?? [];

  const productVendorOptions = productRequestVendorListItems.map((item: string) => ({
    value: item ?? '',
    name: item ?? '',
  }));

  const handleFilterClick = (typeName: SubscriptionCountStatKey) => () => {
    // isAfter: 특정날짜보다 이전 인지
    // isBefore : 특정날짜보다 이후 인지
    // isSame : 특정 날짜와 일치한지
    if (typeName === 'totalCount') {
      // '전체'
      clearQueries();
    } else if (typeName === 'subscriptionCount') {
      // '구독 중'
      setQueries({
        startDateTo: dayjs().format('YYYY-MM-DD'),
        startDateFrom: undefined,
        endDateTo: undefined,
        endDateFrom: dayjs().format('YYYY-MM-DD'),
        status: undefined,
        hasOnDemand: true,
      });
    } else if (typeName === 'monthlyStartingCount') {
      // '구독 시작 예정'
      setQueries({
        startDateTo: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        startDateFrom: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        endDateTo: undefined,
        endDateFrom: undefined,
        status: undefined,
        hasOnDemand: false,
      });
    } else if (typeName === 'monthlyEndingCount') {
      // '구독 종료 예정'
      setQueries({
        startDateTo: undefined,
        startDateFrom: undefined,
        endDateTo: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        endDateFrom: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        status: undefined,
        hasOnDemand: false,
      });
    } else if (typeName === 'endSubscriptionCount') {
      // '구독 종료'
      setQueries({
        startDateTo: undefined,
        startDateFrom: undefined,
        endDateTo: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        endDateFrom: undefined,
        status: undefined,
        hasOnDemand: false,
      });
    }
    setClickedType(typeName);
    handleChangeProductVendor({
      value: '',
      name: '',
    });
  };

  const handleTableFilterUpdate = (filter: Partial<Filters>) => {
    setClickedType('totalCount');

    setQueries(prev => ({ ...prev, ...filter }));
  };

  const subscriptionList: SubscriptionListModel[] =
    subscriptionListPage?.pages.map(({ content }) => content).flat(1) ?? [];

  const handleEndReached = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };
  const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQueries(prev => ({ ...prev, keyword: event.target.value }));
  };

  const subscriptionStatusKeys = isSuccess ? ObjectUtil.keys(subscriptionCountStat.subscriptionTypeName) : [];

  const getCardClassName = (currentStatusKey: string, index: number) => {
    if (clickedType === currentStatusKey) {
      return 'clicked';
    }

    const clickedIndex = subscriptionStatusKeys.findIndex(key => key === clickedType);
    if (clickedIndex - 1 === index) {
      return 'prevButton';
    }

    return '';
  };

  const handleChangeProductVendor = (value: { value: string; name: string } | null) => {
    setQueries(prev => ({ ...prev, productVendorName: value?.value ?? undefined }));
  };

  const handleScrollProductVendor = (event: React.UIEvent<HTMLUListElement>) => {
    if (
      DomUtil.isScrollBottom(event.target as HTMLUListElement) &&
      !isProductVendorFetchingNextPage &&
      isProductVendorHasNextPage
    ) {
      fetchNextProductVendorPage();
    }
  };
  return (
    <>
      <Container ref={setScrollTarget}>
        <Content>
          <SectionTitle>{t('Subscription.menu.list')}</SectionTitle>
          <CardContainer>
            {isSuccess
              ? subscriptionStatusKeys.map((statusKey, index) => (
                  <SubscriptionFilterCard
                    key={statusKey}
                    status={statusKey}
                    statusName={subscriptionCountStat.subscriptionTypeName[statusKey]}
                    count={subscriptionCountStat[statusKey]}
                    monthlyIncrementRate={subscriptionCountStat.monthlyIncrement}
                    onClick={statusKey === 'errorSubscriptionCount' ? undefined : handleFilterClick(statusKey)}
                    // onMouseEnter={handleMouseEnter(typeName)}
                    className={getCardClassName(statusKey, index)}
                  />
                ))
              : null}
          </CardContainer>
          <HeaderContainer>
            <LeftContainer>
              <NumOfSubscription>
                {t('Acc_Main_12')}
                <span>{subscriptionListPage?.pages[0].totalElements}</span>
              </NumOfSubscription>
              <CustomFilterSelect
                id="productVendorList"
                data={productVendorOptions}
                onChange={handleChangeProductVendor}
                placeholder={t('Subscription_Filter.Vendor_Select')}
                onScroll={(event: React.UIEvent<HTMLUListElement>) => handleScrollProductVendor(event)}
                isLoading={isProductVendorLoading}
                value={{
                  value: queries.productVendorName ?? '',
                  name: queries.productVendorName ?? '',
                }}
              />
            </LeftContainer>
            <div className="right-wrapper">
              <TextField
                size="small"
                placeholder={t('Subscrib_Connect_Create_01')}
                value={queries.keyword}
                onChange={handleKeywordChange}
                InputProps={{
                  startAdornment: (
                    <IconWrapper>
                      <SearchIcon width={16} height={16} color={colors['ic-gray-light']} />
                    </IconWrapper>
                  ),
                }}
              />
              <Button variant="contain" size="small" paddingHorizontal={16} onClick={handleAddModal.open}>
                {t('Subscrib_Create_01')}
              </Button>
            </div>
          </HeaderContainer>
          <SubscriptionTable
            isLoading={isSubscriptionListPageLoading}
            filter={queries}
            subscriptionList={subscriptionList}
            scrollTarget={scrollTarget}
            onUpdateFilter={handleTableFilterUpdate}
            endReached={handleEndReached}
          />
        </Content>
      </Container>
      <AddExistingSubscriptionModal open={isAddModalOpen} onClose={handleAddModal.close} />
    </>
  );
};

const Container = styled.section`
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
  padding: 28px 32px;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
`;
const SectionTitle = styled('strong')`
  ${fonts.Headline5};
  margin: 0px 0px 14px 8px;
`;
const CardContainer = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  overflow: auto;
  margin-bottom: 48px;
`;
const HeaderContainer = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  & > .right-wrapper {
    display: flex;
    gap: 8px;
  }
`;
const NumOfSubscription = styled('strong')`
  /* margin-left: 8px; */
  /* margin-top: 6px; */
  ${fonts.Headline7}
  color: ${({ theme }) => theme.colors['text-gray-main']};

  & span {
    margin-left: 4px;
    color: ${({ theme }) => theme.colors['text-primary-light']};
  }
`;
const IconWrapper = styled('div')`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
`;

const LeftContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
`;
